import { ServicePageTemplate } from "@/components/ServicePageTemplate";
import { MapPin, Search, TrendingUp, Map, Star, Globe, Building, Eye, MessageSquare, CheckCircle, ArrowRight } from "lucide-react";
import { GbpRankingStats } from "@/components/GbpRankingStats";
import { motion } from "framer-motion";

const features = [
  {
    icon: MapPin,
    title: "Profile Optimisation",
    description: "Complete Google Business Profile setup and optimisation to maximise your visibility in local search results.",
  },
  {
    icon: Search,
    title: "Category Optimisation",
    description: "Strategic primary and secondary category selection so Google understands exactly what services you offer.",
  },
  {
    icon: Globe,
    title: "Service Keyword Expansion",
    description: "Targeted keyword research and service area expansion so you show up for every relevant search in your area.",
  },
  {
    icon: Map,
    title: "Google Posts",
    description: "Regular Google posts to keep your profile active, signal relevance to Google, and engage potential customers.",
  },
  {
    icon: Star,
    title: "Photo Optimisation",
    description: "Professionally optimised photos that showcase your work, build trust, and help your profile stand out in search results.",
  },
  {
    icon: MessageSquare,
    title: "Q&A SEO Strategy",
    description: "Proactive Q&A management to answer common customer questions and boost your profile's relevance for key search terms.",
  },
  {
    icon: Eye,
    title: "Competitor Monitoring",
    description: "We track what your competitors are doing so you stay one step ahead in your local market.",
  },
  {
    icon: Building,
    title: "Local Citation Building",
    description: "Consistent business listings across 50+ directories to strengthen your local authority and boost rankings.",
  },
  {
    icon: TrendingUp,
    title: "Profile Health & Monthly Reporting",
    description: "Ongoing profile health monitoring with monthly ranking reports so you always know where you stand and how you're growing.",
  },
];

const benefits = [
  "Higher visibility in Google Maps for your key suburbs",
  "More organic enquiries without paying for ads",
  "Stronger presence in local search across your service area",
  "Fully optimised Google Business Profile",
  "Consistent citations across 50+ directories",
  "Monthly reporting on rankings and traffic",
  "Compounding growth — results build month after month",
  "Less reliance on paid advertising over time",
];

export default function GbpSeo() {
  return (
    <ServicePageTemplate
      title="Google Business Profile & Local SEO"
      subtitle="Get found in local search when customers look for your services. Climb the Google Maps rankings and grow your local visibility."
      metaTitle="Google Business Profile & Local SEO | FullScale Marketing"
      metaDescription="Local SEO and Google Business Profile optimisation for Australian trades and local service businesses. Higher visibility in Google Maps and first-page presence in your suburbs."
      canonicalUrl="https://fullscalemarketing.com.au/services/gbp-seo"
      badgeText="Local SEO & GBP"
      icon={MapPin}
      introText="When someone in your suburb Googles 'electrician near me', 'cleaner in [suburb]' or 'gym near me' — are you showing up? Local SEO and Google Business Profile optimisation are the foundation of getting found online. We help you climb the local rankings, look trustworthy and earn the call. Pair this with a SMART website and automated review collection to build a strong online presence over time."
      features={features}
      benefits={benefits}
      bestFor="Plumbers, sparkies, builders, roofers, HVAC, cleaners, salons, gyms, pest control — any local business that relies on Google to be found."
      process={[
        { step: "01", title: "Onboarding Call", text: "We map your suburbs, services and competitors so the strategy targets the right work." },
        { step: "02", title: "GBP & SEO Setup", text: "Profile optimisation, citations, schema and on-page work — all done for you in the first 2 weeks." },
        { step: "03", title: "Launch & Track", text: "Rankings monitored weekly across your priority suburbs and keywords." },
        { step: "04", title: "Monthly Reporting", text: "Clear monthly reports — rankings, calls, direction requests and what we're doing next." },
      ]}
      ctaHeading="Ready to Be More Visible in Local Search?"
      ctaText="Book a free strategy call and we'll audit your current Local SEO and show you exactly how to grow your visibility on Google Maps."
      extraContent={
        <>
          <GbpRankingStats />
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-10"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              Get a Free GBP Audit to See What to Fix to Climb the Local Rankings
            </h2>
            <div className="space-y-3 mt-6 mb-8">
              {[
                "AI-powered, only takes 60 seconds to get.",
                "Audit your GBP settings and actions to get an optimization score.",
                "Get a heatmap audit of how well you are currently ranking locally for top keywords.",
                "Get a personalized action plan to improve your GBP and rankings for free.",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <p className="text-foreground/80 text-base">{item}</p>
                </div>
              ))}
            </div>
            <a
              href="https://audit.gmb.fullscalemarketing.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-8 py-4 rounded-xl shadow-button hover:shadow-button-hover hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 text-base sm:text-lg"
            >
              Get My Free GBP Audit
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.section>
        </>
      }
      relatedServices={[
        {
          icon: Globe,
          title: "SMART Websites",
          description: "A professional website amplifies your local SEO results and converts visitors into leads.",
          href: "/services/websites",
        },
        {
          icon: Star,
          title: "Reputation & Reviews",
          description: "More Google reviews boost your Maps ranking and build trust with potential customers.",
          href: "/services/reputation",
        },
        {
          icon: TrendingUp,
          title: "Lead Generation Ads",
          description: "Combine organic SEO with targeted ads for maximum lead flow in your area.",
          href: "/services/lead-generation",
        },
      ]}
      faqs={[
        {
          question: "How long does it take to rank on Google Maps?",
          answer: "Most businesses start to see ranking improvements within 2–4 months. Local SEO is a compounding strategy — it builds month over month as your authority and review profile grow. Significant gains typically take several months, not weeks.",
        },
        {
          question: "Do I need a website for Google Business Profile SEO?",
          answer: "A website helps, but it's not required to start. We optimise your Google Business Profile independently, and having a SMART Website will amplify your results further.",
        },
        {
          question: "What's the difference between Google Ads and local SEO?",
          answer: "Google Ads are paid placements — you pay per click. Local SEO is organic — once you rank, you get free leads without ongoing ad spend. We recommend both for maximum visibility.",
        },
        {
          question: "How do you track my rankings?",
          answer: "We use professional rank-tracking tools to monitor your Google Maps and local search positions weekly. You'll receive monthly reports showing keyword rankings, visibility changes, and traffic growth.",
        },
        {
          question: "Can you help if I have multiple locations?",
          answer: "Absolutely. We manage multi-location Google Business Profiles and tailor SEO strategies for each service area to maximise coverage across all your target suburbs.",
        },
      ]}
    />
  );
}
